// src/utils/profileAccessControl.js
/**
 * Profile Access Control Utility
 *
 * Determines what a user can view and edit on another employee's profile
 * based on their role and relationship to the employee.
 */

/**
 * Get profile access permissions for a user viewing another employee's profile
 *
 * @param {Object} currentUser - The logged-in user
 * @param {Object} profileEmployee - The employee whose profile is being viewed
 * @param {Array} allEmployees - All employees (to check reporting relationships)
 * @returns {Object} Access permissions
 */
export const getProfileAccessPermissions = (currentUser, profileEmployee, allEmployees = []) => {
    // If viewing own profile, full access
    const isOwnProfile = currentUser?.id === profileEmployee?.id;

    if (isOwnProfile) {
        return {
            isOwnProfile: true,
            canViewAllTabs: true,
            canViewLimitedPanels: false,
            canEditProfile: true,
            canEditJob: true,
            canEditTime: true,
            canEditDocuments: true,
            canAwardCompOff: false,
            showEditButton: true,
            role: 'self'
        };
    }

    // Get user's role/access level
    // Check multiple possible properties for admin status
    const isAdmin = currentUser?.is_admin ||
                    currentUser?.isAdmin ||
                    currentUser?.accessLevel === 'admin' ||
                    currentUser?.access_level === 'admin' ||
                    currentUser?.role === 'admin' ||
                    currentUser?.role_name === 'Admin' ||
                    currentUser?.role_name === 'Super Admin' ||
                    currentUser?.role_name === 'HR Admin' ||
                    (currentUser?.role_name && currentUser.role_name.toLowerCase().includes('admin'));

    // Check if user is a manager
    const isManager = currentUser?.is_manager ||
                      currentUser?.isManager ||
                      currentUser?.accessLevel === 'manager' ||
                      currentUser?.access_level === 'manager' ||
                      currentUser?.role === 'manager' ||
                      currentUser?.role_name === 'Manager';

    const userRole = isAdmin ? 'admin' : (isManager ? 'manager' : (currentUser?.accessLevel || currentUser?.access_level || currentUser?.role || 'employee'));

    // Check if current user is the reporting manager of profile employee
    const isReportingManager = profileEmployee?.reporting_manager_id === currentUser?.id ||
                                profileEmployee?.managerId === currentUser?.id;

    // Check if profile employee reports to current user (direct report)
    const isDirectReport = isReportingManager;

    // Admin Access - Full access to everything
    // IMPORTANT: Admin access ALWAYS overrides any other role (including reporting manager)
    if (isAdmin || userRole === 'admin') {
        return {
            isOwnProfile: false,
            canViewAllTabs: true,
            canViewLimitedPanels: false, // Admin has FULL access, not limited
            canEditProfile: true,
            canEditJob: true,
            canEditTime: true,
            canEditDocuments: true,
            canAwardCompOff: true, // Admin CAN award comp-off
            showEditButton: true,
            role: 'admin'
        };
    }

    // Reporting Manager Access (regardless of portal access level)
    // OR Manager Portal Access
    // NOTE: This is only reached if user is NOT admin (admin access checked first above)
    if (isReportingManager || isManager || userRole === 'manager') {
        return {
            isOwnProfile: false,
            canViewAllTabs: true,
            canViewLimitedPanels: true, // Show limited panels only
            canEditProfile: false, // No edit button for profile tab
            canEditJob: false, // No edit for job tab (can only view employment details section)
            canEditTime: false, // No edit for time tab
            canEditDocuments: false, // No edit for documents
            canAwardCompOff: isDirectReport, // Only for direct reports
            showEditButton: false, // No edit button shown at all
            showLimitedJobSection: true, // Only show employment details in Job tab
            role: isReportingManager ? 'reporting-manager' : 'manager'
        };
    }

    // Employee Portal Access - Most limited
    return {
        isOwnProfile: false,
        canViewAllTabs: true,
        canViewLimitedPanels: true, // Show limited panels only
        canEditProfile: false,
        canEditJob: false,
        canEditTime: false,
        canEditDocuments: false,
        canAwardCompOff: false, // No award comp-off button
        showEditButton: false, // No edit button
        showLimitedJobSection: true, // Only show employment details in Job tab
        role: 'employee'
    };
};

/**
 * Check if user can view a specific tab
 *
 * @param {string} tabId - The tab identifier
 * @param {Object} permissions - Access permissions from getProfileAccessPermissions
 * @returns {boolean}
 */
export const canViewTab = (tabId, permissions) => {
    // For Time and Documents tabs - ONLY self or admin can view
    if (tabId === 'time' || tabId === 'documents') {
        return permissions.isOwnProfile || permissions.role === 'admin';
    }

    // Logs tab - only admin or manager can view (and not on own profile)
    if (tabId === 'logs') {
        return !permissions.isOwnProfile && (permissions.role === 'admin' || permissions.role === 'manager' || permissions.role === 'reporting-manager');
    }

    // All other tabs (About, Profile, Job) are visible based on canViewAllTabs
    return permissions.canViewAllTabs;
};

/**
 * Check if user can edit a specific tab
 *
 * @param {string} tabId - The tab identifier
 * @param {Object} permissions - Access permissions from getProfileAccessPermissions
 * @returns {boolean}
 */
export const canEditTab = (tabId, permissions) => {
    switch (tabId) {
        case 'about':
        case 'profile':
            return permissions.canEditProfile;
        case 'job':
            return permissions.canEditJob;
        case 'time':
            return permissions.canEditTime;
        case 'documents':
            return permissions.canEditDocuments;
        default:
            return false;
    }
};

/**
 * Check if specific content should be hidden based on permissions
 *
 * @param {string} contentType - Type of content (e.g., 'topPanel', 'aboutDetails', 'jobCompensation')
 * @param {Object} permissions - Access permissions from getProfileAccessPermissions
 * @returns {boolean} true if content should be hidden
 */
export const shouldHideContent = (contentType, permissions) => {
    // Admin sees everything
    if (permissions.role === 'admin') {
        return false;
    }

    // Self sees everything on own profile
    if (permissions.isOwnProfile) {
        return false;
    }

    // For non-admins viewing others' profiles with limited view
    if (permissions.canViewLimitedPanels) {
        switch (contentType) {
            // Hide all Profile tab sections for limited users
            case 'profileTab':
                return true;

            // In About tab, hide additional details beyond summary
            case 'aboutTimeline':
            case 'aboutWall':
                return true;

            // In Job tab, hide everything except employment details
            case 'jobWorkAttendance':
            case 'jobCompensation':
            case 'jobBankDetails':
            case 'jobTaxInfo':
                return permissions.showLimitedJobSection;

            // Time tab - all content visible but editing restricted
            case 'timeTab':
                return false;

            // Documents - all visible
            case 'documentsTab':
                return false;

            default:
                return false;
        }
    }

    return false;
};

const profileAccessControl = {
    getProfileAccessPermissions,
    canViewTab,
    canEditTab,
    shouldHideContent
};

export default profileAccessControl;
